import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  Plus, Trash2, Users, Briefcase, Building2, Search,
  ExternalLink, Shield, Eye, Edit, X, Check, Megaphone, RefreshCw
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ProgramSection, sectionActionClass } from "./ProgramSection";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const ROLE_OPTIONS = [
  { value: "PM_PRIMARY", label: "Primary PM", type: "PROJECT_MANAGER", icon: Briefcase },
  { value: "PM_SECONDARY", label: "Secondary PM", type: "PROJECT_MANAGER", icon: Briefcase },
  { value: "PM_MARKETING", label: "Marketing PM", type: "PROJECT_MANAGER", icon: Megaphone },
  { value: "ARTIST", label: "Artist", type: "ARTIST", icon: Users },
  { value: "STUDIO_VENDOR", label: "Studio Vendor", type: "STUDIO", icon: Building2 }
];

const TYPE_ICONS = {
  PROJECT_MANAGER: Briefcase,
  ARTIST: Users,
  STUDIO: Building2
};

const TYPE_COLORS = {
  PROJECT_MANAGER: "text-purple-600 bg-purple-50",
  ARTIST: "text-blue-600 bg-blue-50",
  STUDIO: "text-orange-600 bg-orange-50",
  MARKETING_PM: "text-pink-600 bg-pink-50"
};

function AssignmentCard({ assignment, onRemove, onNavigate }) {
  const TypeIcon = TYPE_ICONS[assignment.resource_type] || Users;
  
  return (
    <div 
      data-testid={`assignment-${assignment.id}`}
      className="flex items-center justify-between p-3 border rounded-lg hover:bg-secondary/30 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`p-2 rounded-full ${TYPE_COLORS[assignment.resource_type] || "bg-gray-100"}`}>
          <TypeIcon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">{assignment.resource_name}</p>
            <Badge variant="outline" className="text-[10px] flex-shrink-0">
              {assignment.role_on_program.replace("_", " ")}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {assignment.can_view_financials && (
              <Badge variant="secondary" className="text-[10px]">
                <Eye className="w-2.5 h-2.5 mr-0.5" />
                Financials
              </Badge>
            )}
            {assignment.can_edit_program && (
              <Badge variant="secondary" className="text-[10px]">
                <Edit className="w-2.5 h-2.5 mr-0.5" />
                Edit
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onNavigate(assignment.resource_id)}
          className="h-8 w-8 p-0"
          data-testid={`view-resource-${assignment.id}`}
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(assignment.id)}
          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
          data-testid={`remove-assignment-${assignment.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function ProgramAssignments({ programId, programName }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [assignments, setAssignments] = useState([]);
  const [scopeModules, setScopeModules] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Form state for new assignment
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedResource, setSelectedResource] = useState("");
  const [canViewFinancials, setCanViewFinancials] = useState(false);
  const [canEditProgram, setCanEditProgram] = useState(true);
  const [notes, setNotes] = useState("");

  const canManage = user?.role === "COO" || user?.role === "Program Manager";

  const fetchData = useCallback(async (showRefreshState = false) => {
    if (showRefreshState) setRefreshing(true);
    try {
      const [assignRes, scopeRes] = await Promise.all([
        apiClient.get(`/programs/${programId}/assignments`),
        apiClient.get(`/programs/${programId}/scope-modules`)
      ]);
      setAssignments(assignRes.data);
      setScopeModules(scopeRes.data);
    } catch (error) {
      toast.error("Failed to load assignments");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [programId]);

  const handleRefresh = () => fetchData(true);

  useEffect(() => {
    fetchData();
    
    // Refresh data when page becomes visible (handles external changes)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    
    // Also poll every 30 seconds for real-time updates
    const pollInterval = setInterval(fetchData, 30000);
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(pollInterval);
    };
  }, [fetchData]);

  async function fetchResources(resourceType) {
    try {
      const response = await apiClient.get(`/resources?resource_type=${resourceType}&status=Active`);
      setResources(response.data);
    } catch (error) {
      toast.error("Failed to load resources");
      setResources([]);
    }
  }

  function handleRoleChange(role) {
    setSelectedRole(role);
    setSelectedResource("");
    
    const roleConfig = ROLE_OPTIONS.find(r => r.value === role);
    if (roleConfig) {
      fetchResources(roleConfig.type);
      // Default financials off for outsourced PMs
      if (role === "PM_SECONDARY") {
        setCanViewFinancials(false);
      }
    }
  }

  async function handleAddAssignment() {
    if (!selectedRole || !selectedResource) {
      toast.error("Please select both a role and a resource");
      return;
    }
    
    setSaving(true);
    try {
      const response = await apiClient.post(`/programs/${programId}/assignments`, {
        program_id: programId,
        resource_id: selectedResource,
        role_on_program: selectedRole,
        can_view_financials: canViewFinancials,
        can_edit_program: canEditProgram,
        notes: notes || null
      });
      
      setAssignments([...assignments, response.data]);
      toast.success("Resource assigned to program");
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign resource");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveAssignment(assignmentId) {
    if (!confirm("Remove this resource from the program?")) return;
    
    try {
      await apiClient.delete(`/programs/${programId}/assignments/${assignmentId}`);
      setAssignments(assignments.filter(a => a.id !== assignmentId));
      toast.success("Assignment removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove assignment");
    }
  }

  function handleCloseDialog() {
    setShowAddDialog(false);
    setSelectedRole("");
    setSelectedResource("");
    setCanViewFinancials(false);
    setCanEditProgram(true);
    setNotes("");
    setResources([]);
    setSearch("");
  }

  // Filter already assigned resources
  const assignedResourceIds = assignments.map(a => a.resource_id);
  const availableResources = resources.filter(r => 
    !assignedResourceIds.includes(r.id) &&
    (search === "" || 
      r.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.email?.toLowerCase().includes(search.toLowerCase()))
  );

  // Group assignments by type
  const pmAssignments = assignments.filter(a => a.role_on_program.startsWith("PM"));
  const artistAssignments = assignments.filter(a => a.role_on_program === "ARTIST");
  const studioAssignments = assignments.filter(a => a.role_on_program === "STUDIO_VENDOR");

  // Get scope module PMs (only Marketing PM, not Visualization)
  // Visualization PM should be the Primary PM under Project Managers
  const scopePMs = scopeModules
    .filter(m => m.is_included && m.assigned_pm_user_id && m.assigned_pm_name && m.scope_type === "MARKETING")
    .map(m => ({
      id: `scope-${m.scope_type}`,
      resource_id: m.assigned_pm_user_id,
      resource_name: m.assigned_pm_name,
      resource_type: "PROJECT_MANAGER",
      role_on_program: `${m.scope_type}_PM`,
      scope_type: m.scope_type,
      is_scope_pm: true
    }));

  if (loading) {
    return (
      <div className="rounded-2xl border border-border py-8 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <ProgramSection
        title="Team & Resources"
        data-testid="program-assignments"
        action={
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-8 w-8 rounded-full p-0 text-muted-foreground hover:bg-secondary hover:text-foreground"
              title="Refresh"
              data-testid="refresh-assignments-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            {canManage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddDialog(true)}
                className={sectionActionClass}
                data-testid="add-assignment-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Assign
              </Button>
            )}
          </div>
        }
      >
          {assignments.length === 0 && scopePMs.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No resources assigned yet</p>
              {canManage && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setShowAddDialog(true)}
                  className="mt-2"
                >
                  Assign team members
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Marketing PM (from scope modules) */}
              {scopePMs.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                    <Megaphone className="w-3 h-3" /> Marketing PM
                  </p>
                  <div className="space-y-2">
                    {scopePMs.map(pm => (
                      <div 
                        key={pm.id}
                        data-testid={`scope-pm-${pm.scope_type}`}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-secondary/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`p-2 rounded-full ${TYPE_COLORS.MARKETING_PM}`}>
                            <Megaphone className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium truncate">{pm.resource_name}</p>
                              <Badge variant="outline" className="text-[10px] flex-shrink-0 bg-pink-50">
                                {pm.scope_type} PM
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">Auto-assigned for {pm.scope_type.toLowerCase()} scope</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/studio/resources/${pm.resource_id}`)}
                          className="h-8 w-8 p-0"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Project Managers */}
              {pmAssignments.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> Project Managers
                  </p>
                  <div className="space-y-2">
                    {pmAssignments.map(assignment => (
                      <AssignmentCard
                        key={assignment.id}
                        assignment={assignment}
                        onRemove={handleRemoveAssignment}
                        onNavigate={(id) => navigate(`/studio/resources/${id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Artists */}
              {artistAssignments.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                    <Users className="w-3 h-3" /> Artists
                  </p>
                  <div className="space-y-2">
                    {artistAssignments.map(assignment => (
                      <AssignmentCard
                        key={assignment.id}
                        assignment={assignment}
                        onRemove={handleRemoveAssignment}
                        onNavigate={(id) => navigate(`/studio/resources/${id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Studios */}
              {studioAssignments.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> Studio Vendors
                  </p>
                  <div className="space-y-2">
                    {studioAssignments.map(assignment => (
                      <AssignmentCard
                        key={assignment.id}
                        assignment={assignment}
                        onRemove={handleRemoveAssignment}
                        onNavigate={(id) => navigate(`/studio/resources/${id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
      </ProgramSection>

      {/* Add Assignment Dialog */}
      <Dialog open={showAddDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Resource</DialogTitle>
            <DialogDescription>
              Add a team member or vendor to {programName || "this program"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Role Selection */}
            <div className="space-y-1.5">
              <Label>Role on Program *</Label>
              <Select value={selectedRole} onValueChange={handleRoleChange}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <span className="flex items-center gap-2">
                        <role.icon className="w-4 h-4" />
                        {role.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Resource Selection */}
            {selectedRole && (
              <div className="space-y-1.5">
                <Label>Resource *</Label>
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or email..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="border rounded-md max-h-[200px] overflow-y-auto">
                    {availableResources.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {resources.length === 0 
                          ? "Loading resources..." 
                          : search 
                            ? "No matching resources found" 
                            : "All resources already assigned"}
                      </p>
                    ) : (
                      availableResources.map(resource => (
                        <div
                          key={resource.id}
                          onClick={() => setSelectedResource(resource.id)}
                          className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-secondary/50 ${
                            selectedResource === resource.id ? "bg-secondary" : ""
                          }`}
                          data-testid={`resource-option-${resource.id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{resource.display_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{resource.email}</p>
                          </div>
                          {selectedResource === resource.id && (
                            <Check className="w-4 h-4 text-primary flex-shrink-0" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Permissions */}
            {selectedResource && (
              <div className="space-y-3 pt-2 border-t">
                <p className="text-sm font-medium">Permissions</p>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="view-financials"
                    checked={canViewFinancials}
                    onChange={e => setCanViewFinancials(e.target.checked)}
                    className="rounded"
                  />
                  <Label htmlFor="view-financials" className="text-sm font-normal cursor-pointer">
                    Can view program financials
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="edit-program"
                    checked={canEditProgram}
                    onChange={e => setCanEditProgram(e.target.checked)}
                    className="rounded"
                  />
                  <Label htmlFor="edit-program" className="text-sm font-normal cursor-pointer">
                    Can edit program details
                  </Label>
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedResource && (
              <div className="space-y-1.5">
                <Label>Notes (optional)</Label>
                <Input
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="e.g., Lead on interiors, weekends only"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddAssignment} 
              disabled={!selectedRole || !selectedResource || saving}
              data-testid="confirm-assignment-btn"
            >
              {saving ? "Assigning..." : "Assign Resource"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
